// TMOS Notification Bell Component
// Date: December 9, 2025
// Purpose: Bell icon with unread notification count indicator
//
// Features:
// - Bell icon in top navigation
// - Unread count badge (red circle with number)
// - Click to open notification dropdown
// - Real-time unread count updates

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BellIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '@/state/language/LanguageProvider';
import { useAuth } from '@/state/auth/AuthProvider';
import api from '@/services/api';

interface Notification {
  id: string;
  notification_type: string;
  title: string;
  message: string;
  status: 'UNREAD' | 'READ' | 'ARCHIVED';
  created_at: string;
  entity_type?: string;
  entity_id?: string;
}

/**
 * NotificationBell Component
 * 
 * Displays bell icon with unread notification count.
 * Clicking opens a dropdown with recent notifications.
 * 
 * Business Rules:
 * - Shows unread count badge when count > 0
 * - Polls for unread count every 30 seconds
 * - Click opens notification dropdown
 */
export const NotificationBell: React.FC = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  /**
   * Load unread notification count
   */
  const loadUnreadCount = useCallback(async () => {
    try {
      const response = await api.get('/user-notifications/unread-count');
      const count = response.data?.count || response.data?.data?.count || 0;
      setUnreadCount(count);
    } catch (error: any) {
      // Fallback to admin notifications if user notifications fail
      try {
        const fallbackResponse = await api.get('/notifications/unread-count');
        setUnreadCount(fallbackResponse.data.count || 0);
      } catch (fallbackError) {
        // Don't show error to user - just keep current count
      }
    }
  }, []);

  // Load unread count on mount and poll every 30 seconds (or 5 seconds when dropdown is open)
  // Only load if user is authenticated
  useEffect(() => {
    // Don't load if auth is still loading or user is not authenticated
    if (authLoading || !isAuthenticated) {
      return;
    }

    // Call immediately
    loadUnreadCount().catch(() => {
      // Silently handle errors
    });
    
    const pollInterval = isOpen ? 5000 : 30000; // Poll every 5 seconds when open, 30 seconds when closed
    const interval = setInterval(() => {
      loadUnreadCount().catch(() => {
        // Silently handle errors
      });
    }, pollInterval);
    
    return () => {
      clearInterval(interval);
    };
  }, [isOpen, loadUnreadCount, isAuthenticated, authLoading]);

  /**
   * Load recent notifications (both unread and recent read)
   */
  const loadNotifications = useCallback(async () => {
    try {
      // Only show loading spinner on initial load, not on polling updates
      if (notifications.length === 0) {
        setLoading(true);
      }
      const response = await api.get('/user-notifications?limit=20');
      const fetchedNotifications = response.data.data || [];
      setNotifications(fetchedNotifications);
      
      // Update unread count based on fetched notifications
      const unread = fetchedNotifications.filter((n: Notification) => n.status === 'UNREAD').length;
      setUnreadCount(unread);
    } catch (error: any) {
      // Fallback to admin notifications if user notifications fail
      try {
        const fallbackResponse = await api.get('/notifications/active');
        setNotifications(fallbackResponse.data.data || []);
      } catch (fallbackError) {
        // Silently handle errors
      }
    } finally {
      setLoading(false);
    }
  }, [notifications.length]);

  // Load notifications when dropdown opens and poll for updates
  useEffect(() => {
    // Don't load if auth is still loading or user is not authenticated
    if (authLoading || !isAuthenticated) {
      return;
    }

    if (isOpen) {
      loadNotifications();
      // Poll for new notifications every 5 seconds when dropdown is open
      const interval = setInterval(loadNotifications, 5000);
      return () => clearInterval(interval);
    }
  }, [isOpen, loadNotifications, isAuthenticated, authLoading]);

  /**
   * Mark notification as read
   */
  const markAsRead = async (notificationId: string) => {
    try {
      await api.put(`/user-notifications/${notificationId}/read`);
      setUnreadCount(prev => Math.max(0, prev - 1));
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
    } catch (error) {
      // Silently handle errors
    }
  };

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = async () => {
    try {
      await api.put('/user-notifications/mark-all-read');
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, status: 'READ' as const })));
    } catch (error) {
      // Silently handle errors
    }
  };

  /**
   * Handle notification click - navigate to entity and mark as read
   */
  const handleNotificationClick = async (notification: Notification) => {
    await markAsRead(notification.id);
    
    // Navigate to entity based on entity type
    if (notification.entity_type && notification.entity_id) {
      switch (notification.entity_type.toLowerCase()) {
        case 'discussion':
          navigate(`/discussions?id=${notification.entity_id}`);
          break;
        case 'issue':
          navigate(`/issues/${notification.entity_id}`);
          break;
        case 'action':
          navigate(`/actions/${notification.entity_id}`);
          break;
        case 'risk':
          navigate(`/risk?riskId=${notification.entity_id}`);
          break;
        case 'kpi':
          navigate(`/kpis/${notification.entity_id}`);
          break;
        case 'transformation':
          navigate(`/transformations/${notification.entity_id}`);
          break;
        case 'strategy':
          navigate(`/strategies/${notification.entity_id}`);
          break;
        case 'calendar_event':
          navigate('/calendar');
          break;
        default:
          // Unknown entity type - do nothing
          break;
      }
    }
    
    setIsOpen(false);
  };

  return (
    <div 
      className="relative" 
      data-testid="notification-bell"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      {/* Bell Icon Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-gray-600 hover:text-gray-900 focus:outline-none"
        aria-label={t('notifications.bell') || 'Notifications'}
        title={`Notifications (${unreadCount} unread)`}
      >
        <BellIcon className="w-6 h-6" />
        {/* Unread Count Badge */}
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 block h-5 w-5 rounded-full bg-red-500 text-white text-xs font-semibold flex items-center justify-center">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Dropdown */}
      {isOpen && (
        <>
          {/* Invisible bridge to prevent gap closure */}
          <div className="absolute right-0 top-full w-full h-2" />
          <div 
            className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-20 max-h-96 overflow-y-auto"
            onMouseEnter={() => setIsOpen(true)}
            onMouseLeave={() => setIsOpen(false)}
          >
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-[var(--color-surface-text)]">
                {t('notifications.title') || 'Notifications'}
              </h3>
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="flex items-center gap-1 text-sm text-blue-600 hover:underline"
                  title={t('notifications.markAllRead') || 'Mark all as read'}
                >
                  <CheckCircleIcon className="h-4 w-4" />
                  {t('notifications.markAllRead') || 'Mark All'}
                </button>
              )}
            </div>

            {loading ? (
              <div className="p-4 text-center text-[var(--color-secondary-text)] text-sm">
                {t('notifications.loading') || 'Loading...'}
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-4 text-center text-[var(--color-secondary-text)] text-sm">
                {t('notifications.noNotifications') || 'No notifications'}
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`p-4 hover:bg-gray-50 cursor-pointer transition-colors ${
                      notification.status === 'UNREAD' ? 'bg-blue-50' : ''
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="text-sm font-semibold text-[var(--color-surface-text)]">
                            {notification.title}
                          </h4>
                          {notification.status === 'UNREAD' && (
                            <span className="h-2 w-2 bg-blue-600 rounded-full"></span>
                          )}
                        </div>
                        <p className="text-xs text-[var(--color-secondary-text)] mt-1">
                          {notification.message}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(notification.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Footer Actions */}
            {notifications.length > 0 && (
              <div className="p-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    // TODO: Navigate to full notifications page when implemented
                    setIsOpen(false);
                  }}
                  className="text-sm text-[var(--color-primary-bg)] hover:underline w-full text-left"
                >
                  {t('notifications.viewAll') || 'View All Notifications'}
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

